import React, { useRef } from 'react';
import { NativeSyntheticEvent, TextInput, TextInputKeyPressEventData, View } from 'react-native';
import { radius, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';

interface Props {
  length?: number;
  value: string;
  onChange: (v: string) => void;
}

export function OtpInput({ length = 6, value, onChange }: Props) {
  const { colors } = useAppTheme();
  const refs = useRef<(TextInput | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const setDigit = (index: number, typed: string) => {
    const clean = typed.replace(/[^0-9]/g, '');
    // A whole code pasted (or filled in by the keyboard from the email): spread it from here.
    if (clean.length > 2) {
      const next = digits.slice();
      clean.slice(0, length - index).split('').forEach((d, i) => (next[index + i] = d));
      onChange(next.join(''));
      refs.current[Math.min(index + clean.length, length) - 1]?.blur();
      return;
    }
    // Typing over a filled box replaces its digit (the box used to refuse a second character, so a
    // wrong code had to be deleted digit by digit before it could be fixed).
    const digit = clean.slice(-1);
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join(''));
    if (digit && index < length - 1) refs.current[index + 1]?.focus();
  };

  const handleKeyPress = (index: number, e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  return (
    <View style={styles.row}>
      {digits.map((digit, index) => (
        <TextInput
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          style={[
            type.h2,
            styles.box,
            {
              backgroundColor: colors.surface,
              borderColor: digit ? colors.accent : colors.border,
              color: colors.textPrimary,
            },
          ]}
          value={digit}
          onChangeText={(v) => setDigit(index, v)}
          onKeyPress={(e) => handleKeyPress(index, e)}
          keyboardType="number-pad"
          textContentType="oneTimeCode"
          autoComplete="sms-otp"
          selectTextOnFocus
          textAlign="center"
          accessibilityLabel={`Digit ${index + 1} of ${length}`}
        />
      ))}
    </View>
  );
}

const styles = ScaledSheet.create({
  row: { flexDirection: 'row', gap: 10, justifyContent: 'center' },
  // Six boxes share the row: 48 wide where there's room, narrower on a 320–360 phone.
  box: {
    flex: 1,
    maxWidth: 48,
    height: 56,
    borderRadius: radius.md,
    borderWidth: 1.5,
  },
});
